import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Plus, Trash2, Save, Loader2, ShoppingBag, Search } from "lucide-react";

const CATS = ["entry", "frame", "theme", "profile_card", "vanity_badge", "nickname_color", "bundles"];
const RARITIES = ["common", "rare", "epic", "legendary"];

/**
 * StoreAdmin — admin-only page to edit prices/rarity/names, add new items,
 * or delete items in the Hifireels Store. Requires admin JWT.
 */
export default function StoreAdmin() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("entry");   // "entry"|"frame"|...|"bundles"
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", price: 500, rarity: "common", category: "entry", payload: {} });
  const [q, setQ] = useState("");
  // ---- Bundles state ----
  const [bundles, setBundles] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [bDrafts, setBDrafts] = useState({});
  const [newBundle, setNewBundle] = useState({ name: "", description: "", price: 1000, item_ids: [] });
  const [creatingBundle, setCreatingBundle] = useState(false);
  const [savingBundle, setSavingBundle] = useState(null);

  const load = () => {
    setLoading(true);
    if (tab === "bundles") {
      Promise.all([
        api.get(`/store/bundles`),
        api.get(`/store/items`),
      ]).then(([b, all]) => { setBundles(b.data || []); setAllItems(all.data || []); })
        .finally(() => setLoading(false));
    } else {
      api.get(`/store/items?category=${tab}`)
        .then(r => setItems(r.data || []))
        .finally(() => setLoading(false));
    }
  };
  useEffect(load, [tab]); // eslint-disable-line

  const patchDraft = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const save = async (item) => {
    const d = drafts[item.id]; if (!d) return;
    setSavingId(item.id);
    try {
      await api.patch(`/admin/store/items/${item.id}`, {
        price: d.price !== undefined ? parseInt(d.price, 10) : undefined,
        name: d.name !== undefined ? d.name : undefined,
        rarity: d.rarity !== undefined ? d.rarity : undefined,
      });
      setDrafts(prev => { const n = { ...prev }; delete n[item.id]; return n; });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingId(null); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/store/items/${item.id}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  // ---- Bundle handlers ----
  const patchBundleDraft = (id, patch) => setBDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  const toggleBundleItem = (id, itemId) => {
    const cur = (bDrafts[id]?.item_ids) || bundles.find(b => b.id === id)?.item_ids || [];
    const next = cur.includes(itemId) ? cur.filter(x => x !== itemId) : [...cur, itemId];
    patchBundleDraft(id, { item_ids: next });
  };
  const saveBundle = async (bundle) => {
    const d = bDrafts[bundle.id]; if (!d) return;
    setSavingBundle(bundle.id);
    try {
      await api.patch(`/admin/store/bundles/${bundle.id}`, {
        name: d.name, description: d.description,
        price: d.price !== undefined ? parseInt(d.price, 10) : undefined,
        item_ids: d.item_ids,
      });
      setBDrafts(prev => { const n = { ...prev }; delete n[bundle.id]; return n; });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingBundle(null); }
  };
  const deleteBundle = async (bundle) => {
    if (!window.confirm(`Delete bundle "${bundle.name}"?`)) return;
    try { await api.delete(`/admin/store/bundles/${bundle.id}`); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };
  const createBundle = async () => {
    if (!newBundle.name || !newBundle.item_ids.length) return alert("Add name and pick items");
    setCreatingBundle(true);
    try {
      await api.post(`/admin/store/bundles`, newBundle);
      setNewBundle({ name: "", description: "", price: 1000, item_ids: [] });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Create failed"); }
    finally { setCreatingBundle(false); }
  };

  const create = async () => {
    if (!newItem.name || newItem.price < 0) return alert("Fill name and non-negative price");
    setCreating(true);
    try {
      await api.post("/admin/store/items", newItem);
      setNewItem({ name: "", price: 500, rarity: "common", category: tab, payload: {} });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const filtered = items.filter(i => !q.trim() || i.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="px-4 py-6 md:max-w-5xl md:mx-auto" data-testid="store-admin-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/admin" className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></Link>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter flex items-center gap-2"><ShoppingBag className="w-6 h-6 text-[#FF7A00]"/>Store Editor</h1>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1.5 overflow-x-auto mb-3">
        {CATS.map(c => (
          <button key={c} onClick={() => setTab(c)} data-testid={`admin-tab-${c}`}
            className={`px-3 py-1.5 rounded-full text-xs font-extrabold ${tab === c ? "bg-[#FF7A00] text-black" : "bg-white/8 text-white/70"}`}>{c}</button>
        ))}
      </div>

      {/* Search — items only */}
      {tab !== "bundles" && (
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search items…"
            data-testid="admin-search"
            className="w-full pl-9 pr-3 py-2 rounded-full bg-white/5 border border-white/10 outline-none text-sm"/>
        </div>
      )}

      {/* Create new item — items only */}
      {tab !== "bundles" && (
        <div className="mb-4 p-3 rounded-2xl bg-white/5 border border-white/10 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="admin-create-row">
        <input value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })}
          placeholder="Name" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" data-testid="admin-new-name"/>
        <input type="number" value={newItem.price} onChange={e => setNewItem({ ...newItem, price: parseInt(e.target.value, 10) || 0 })}
          placeholder="Price" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" data-testid="admin-new-price"/>
        <select value={newItem.rarity} onChange={e => setNewItem({ ...newItem, rarity: e.target.value })}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" data-testid="admin-new-rarity">
          {RARITIES.map(r => <option key={r} value={r} className="bg-black">{r}</option>)}
        </select>
        <select value={newItem.category} onChange={e => setNewItem({ ...newItem, category: e.target.value })}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" data-testid="admin-new-cat">
          {CATS.map(c => <option key={c} value={c} className="bg-black">{c}</option>)}
        </select>
        <button onClick={create} disabled={creating || !newItem.name} data-testid="admin-create-btn"
          className="px-3 py-2 rounded-lg bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-extrabold text-sm disabled:opacity-40 flex items-center justify-center gap-1">
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}Add
        </button>
      </div>
      )}

      {/* Item list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>
      ) : tab === "bundles" ? (
        <div className="space-y-3" data-testid="admin-bundles-view">
          {/* Create new bundle */}
          <div className="p-3 rounded-2xl bg-white/5 border border-white/10 space-y-2" data-testid="admin-new-bundle">
            <div className="text-xs uppercase tracking-wide text-[#FF7A00] font-extrabold">New Bundle</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input value={newBundle.name} onChange={e => setNewBundle({ ...newBundle, name: e.target.value })}
                placeholder="Bundle name (emoji ok)" data-testid="admin-new-bundle-name"
                className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm"/>
              <input type="number" value={newBundle.price} onChange={e => setNewBundle({ ...newBundle, price: parseInt(e.target.value, 10) || 0 })}
                placeholder="Price" data-testid="admin-new-bundle-price"
                className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm font-bold text-[#FF7A00]"/>
            </div>
            <textarea value={newBundle.description} onChange={e => setNewBundle({ ...newBundle, description: e.target.value })}
              rows={2} placeholder="Short pitch shown on the card"
              data-testid="admin-new-bundle-desc"
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm"/>
            <div className="text-[11px] text-white/60">Pick items to include ({newBundle.item_ids.length} selected)</div>
            <div className="max-h-48 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-1.5">
              {allItems.map(it => {
                const on = newBundle.item_ids.includes(it.id);
                return (
                  <button key={it.id} onClick={() => setNewBundle(nb => ({ ...nb, item_ids: on ? nb.item_ids.filter(x => x !== it.id) : [...nb.item_ids, it.id] }))}
                    data-testid={`admin-new-bundle-pick-${it.id}`}
                    className={`text-[10px] p-1.5 rounded-lg text-left ${on ? "bg-[#FF7A00]/20 border border-[#FF7A00]/50" : "bg-black/30 border border-white/10 text-white/70"}`}>
                    {on && <span className="mr-1">✓</span>}
                    {it.name} <span className="text-white/40">· {it.price}g</span>
                  </button>
                );
              })}
            </div>
            <button onClick={createBundle} disabled={creatingBundle || !newBundle.name || !newBundle.item_ids.length}
              data-testid="admin-new-bundle-create"
              className="w-full py-2 rounded-lg bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-extrabold text-sm disabled:opacity-40 flex items-center justify-center gap-1">
              {creatingBundle ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}Create Bundle
            </button>
          </div>

          {/* Existing bundles */}
          {bundles.length === 0 ? <div className="text-white/50 text-center py-6">No bundles yet</div> : bundles.map(b => {
            const d = bDrafts[b.id] || {};
            const dirty = Object.keys(d).length > 0;
            const currentItemIds = d.item_ids || b.item_ids || [];
            return (
              <div key={b.id} data-testid={`admin-bundle-${b.id}`}
                className={`p-3 rounded-2xl bg-gradient-to-br ${b.banner_gradient || "from-[#00E5FF] to-[#A855F7]"} border border-white/15`}>
                <div className="bg-black/60 rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input defaultValue={b.name} onChange={e => patchBundleDraft(b.id, { name: e.target.value })}
                      className="flex-1 px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-sm font-bold" data-testid={`admin-bundle-name-${b.id}`}/>
                    <input type="number" defaultValue={b.price} onChange={e => patchBundleDraft(b.id, { price: parseInt(e.target.value, 10) || 0 })}
                      className="w-24 px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-sm font-bold text-[#FF7A00]" data-testid={`admin-bundle-price-${b.id}`}/>
                    <div className="text-[10px] text-white/50 line-through">{b.regular_price}g</div>
                  </div>
                  <textarea defaultValue={b.description} onChange={e => patchBundleDraft(b.id, { description: e.target.value })}
                    rows={2} className="w-full px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-xs" data-testid={`admin-bundle-desc-${b.id}`}/>
                  <div className="text-[11px] text-white/60">Items ({currentItemIds.length})</div>
                  <div className="max-h-40 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-1.5">
                    {allItems.map(it => {
                      const on = currentItemIds.includes(it.id);
                      return (
                        <button key={it.id} onClick={() => toggleBundleItem(b.id, it.id)}
                          data-testid={`admin-bundle-pick-${b.id}-${it.id}`}
                          className={`text-[10px] p-1.5 rounded-lg text-left ${on ? "bg-[#FF7A00]/20 border border-[#FF7A00]/50" : "bg-black/30 border border-white/10 text-white/60"}`}>
                          {on && <span className="mr-1">✓</span>}{it.name} <span className="text-white/40">· {it.price}g</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => saveBundle(b)} disabled={!dirty || savingBundle === b.id}
                      data-testid={`admin-bundle-save-${b.id}`}
                      className="flex-1 py-1.5 rounded-lg bg-[#00E5FF] text-black text-xs font-extrabold disabled:opacity-40 flex items-center justify-center gap-1">
                      {savingBundle === b.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}Save
                    </button>
                    <button onClick={() => deleteBundle(b)} data-testid={`admin-bundle-del-${b.id}`}
                      className="px-3 py-1.5 rounded-lg bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F]"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-white/50 text-center py-6">No items in this category</div>
      ) : (
        <div className="space-y-2" data-testid="admin-items-list">
          {filtered.map(it => {
            const d = drafts[it.id] || {};
            const dirty = Object.keys(d).length > 0;
            return (
              <div key={it.id} data-testid={`admin-row-${it.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center p-2.5 rounded-xl bg-white/5 border border-white/10">
                <input defaultValue={it.name} onChange={e => patchDraft(it.id, { name: e.target.value })}
                  className="md:col-span-4 px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-sm" data-testid={`admin-name-${it.id}`}/>
                <input type="number" defaultValue={it.price}
                  onChange={e => patchDraft(it.id, { price: parseInt(e.target.value, 10) || 0 })}
                  className="md:col-span-2 px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-sm font-bold text-[#FF7A00]" data-testid={`admin-price-${it.id}`}/>
                <select defaultValue={it.rarity} onChange={e => patchDraft(it.id, { rarity: e.target.value })}
                  className="md:col-span-2 px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-sm" data-testid={`admin-rarity-${it.id}`}>
                  {RARITIES.map(r => <option key={r} value={r} className="bg-black">{r}</option>)}
                </select>
                <div className="md:col-span-2 text-[11px] text-white/50 truncate">{it.category}</div>
                <div className="md:col-span-2 flex items-center gap-1.5">
                  <button onClick={() => save(it)} disabled={!dirty || savingId === it.id}
                    data-testid={`admin-save-${it.id}`}
                    className="flex-1 px-2 py-1.5 rounded-lg bg-[#00E5FF] text-black text-xs font-extrabold disabled:opacity-40 flex items-center justify-center gap-1">
                    {savingId === it.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}Save
                  </button>
                  <button onClick={() => remove(it)} data-testid={`admin-del-${it.id}`}
                    className="px-2 py-1.5 rounded-lg bg-[#FF2A5F]/10 border border-[#FF2A5F]/40 text-[#FF2A5F]"><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
